import type { GeckoComponentDataFrame, GeckoComponentDataset } from 'types'

export interface CdfComponentInspection {
  component: string
  inspection: string
}

/**
 * Resolve the `(component, inspection)` pairs a component-data-frame (CDF)
 * attests to. This is the single source of truth for how a CDF is associated
 * with an inspection across libra, reused by both `useFfsComponentCdfs` (the
 * per-(component, inspection) CDF filter) and `buildInspectionsWithCdfData`
 * (the tab layer's inverse index of which inspections carry CDF data).
 *
 * A CDF attests to a pair EITHER:
 *  - **directly** — its own `component` + `inspection` tags (both truthy), OR
 *  - **via the CDS bridge** — its `component-dataset` tag points at a
 *    `GeckoComponentDataset` whose `component` + `inspection` tags are truthy.
 *
 * The two can differ, so both are returned; callers match against whichever
 * pair they care about. CDFs/CDSes missing the needed tags contribute nothing.
 */
export function resolveCdfComponentInspections(
  cdf: GeckoComponentDataFrame,
  datasetById: ReadonlyMap<string, GeckoComponentDataset>
): CdfComponentInspection[] {
  const pairs: CdfComponentInspection[] = []

  const { component, inspection } = cdf.tags
  if (component && inspection) {
    pairs.push({ component, inspection })
  }

  const datasetId = cdf.tags['component-dataset']
  if (datasetId) {
    const cds = datasetById.get(datasetId)
    if (cds && cds.tags.component && cds.tags.inspection) {
      pairs.push({
        component: cds.tags.component,
        inspection: cds.tags.inspection,
      })
    }
  }

  return pairs
}
